import { redirect } from '@tanstack/react-router';

import { buildDarkwingUrl } from '@/components/navigation/utils';
import { ROUTE_FEATURE_FLAGS } from '@/config/route-feature-flags';

import { identifyUserInLaunchDarkly } from './shared-utils';
import type { RouteGuardContext, RouteGuardParams } from './types';

/**
 * Creates a catch-all route guard that handles undefined routes
 * @returns A beforeLoad function for TanStack Router catch-all routes
 */
export function createCatchAllRouteGuard() {
  return async ({
    context,
    params,
  }: {
    context: RouteGuardContext;
    params: RouteGuardParams & { _splat?: string };
  }) => {
    const { ldClient, auth } = context;
    const routeId = params._splat || '';

    console.log(`[Catch-all Route] Handling undefined route: ${routeId}`);

    // Check if we need to identify the user in LaunchDarkly
    if (ldClient && auth) {
      await identifyUserInLaunchDarkly(ldClient, auth, params, 'Catch-all Route');
    }

    // Check if this route has a feature flag
    const flagKey = ROUTE_FEATURE_FLAGS[routeId];

    if (flagKey && ldClient) {
      // Route has a feature flag, check it
      try {
        const flagValue = ldClient.variation(flagKey, false);
        console.log(`[Catch-all Route] Feature flag ${flagKey} = ${flagValue}`);

        if (flagValue) {
          // Feature is enabled in Gaia, but route doesn't exist yet
          // This shouldn't happen in normal operation, but handle gracefully
          console.warn(`[Catch-all Route] Feature ${routeId} is enabled but route not implemented`);
        }
      } catch (error) {
        console.error(`[Catch-all Route] Error evaluating flag:`, error);
      }
    }

    // Always redirect to Darkwing for catch-all routes
    const darkwingUrl = buildDarkwingUrl(params.org, params.agent, routeId);
    console.log(`[Catch-all Route] Redirecting to Darkwing: ${darkwingUrl}`);

    // Use TanStack Router's redirect with href for external URLs
    throw redirect({
      href: darkwingUrl,
    });
  };
}
